/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ProductTypeEnum } from './ProductTypeEnum';
import {
    ProductTypeEnumFromJSON,
    ProductTypeEnumFromJSONTyped,
    ProductTypeEnumToJSON,
} from './ProductTypeEnum';
import type { MoneyRequest } from './MoneyRequest';
import {
    MoneyRequestFromJSON,
    MoneyRequestFromJSONTyped,
    MoneyRequestToJSON,
} from './MoneyRequest';
import type { NetworkNameTechnicalEnum } from './NetworkNameTechnicalEnum';
import {
    NetworkNameTechnicalEnumFromJSON,
    NetworkNameTechnicalEnumFromJSONTyped,
    NetworkNameTechnicalEnumToJSON,
} from './NetworkNameTechnicalEnum';

/**
 * 
 * @export
 * @interface AdminProductCreationRequest
 */
export interface AdminProductCreationRequest {
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    name: string;
    /**
     * 
     * @type {ProductTypeEnum}
     * @memberof AdminProductCreationRequest
     */
    productType: ProductTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    agioPercent: string;
    /**
     * 
     * @type {MoneyRequest}
     * @memberof AdminProductCreationRequest
     */
    nominalValue: MoneyRequest | null;
    /**
     * 
     * @type {MoneyRequest}
     * @memberof AdminProductCreationRequest
     */
    issuancePrice: MoneyRequest | null;
    /**
     * 
     * @type {boolean}
     * @memberof AdminProductCreationRequest
     */
    provideNominalValue: boolean;
    /**
     * 
     * @type {number}
     * @memberof AdminProductCreationRequest
     */
    maxIssuableAmount: number;
    /**
     * 
     * @type {number}
     * @memberof AdminProductCreationRequest
     */
    stepSize: number;
    /**
     * 
     * @type {number}
     * @memberof AdminProductCreationRequest
     */
    paymentDeadline: number;
    /**
     * 
     * @type {number}
     * @memberof AdminProductCreationRequest
     */
    cancellationDeadline: number;
    /**
     * 
     * @type {NetworkNameTechnicalEnum}
     * @memberof AdminProductCreationRequest
     */
    network: NetworkNameTechnicalEnum;
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    code: string;
    /**
     * 
     * @type {number}
     * @memberof AdminProductCreationRequest
     */
    calculationFactor: number;
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    backupWallet?: string;
    /**
     * 
     * @type {Date}
     * @memberof AdminProductCreationRequest
     */
    lockInTime?: Date;
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    issuanceDocumentId: string;
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    issuerId: string;
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    issuerWalletAddress: string;
    /**
     * 
     * @type {boolean}
     * @memberof AdminProductCreationRequest
     */
    ewpg: boolean;
    /**
     * 
     * @type {string}
     * @memberof AdminProductCreationRequest
     */
    isin?: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof AdminProductCreationRequest
     */
    publicationRestricted: boolean;
}



/**
 * Check if a given object implements the AdminProductCreationRequest interface.
 */
export function instanceOfAdminProductCreationRequest(value: object): value is AdminProductCreationRequest {
    if (!('name' in value) || value['name'] === undefined) return false;
    if (!('productType' in value) || value['productType'] === undefined) return false;
    if (!('agioPercent' in value) || value['agioPercent'] === undefined) return false;
    if (!('nominalValue' in value) || value['nominalValue'] === undefined) return false;
    if (!('issuancePrice' in value) || value['issuancePrice'] === undefined) return false;
    if (!('provideNominalValue' in value) || value['provideNominalValue'] === undefined) return false;
    if (!('maxIssuableAmount' in value) || value['maxIssuableAmount'] === undefined) return false;
    if (!('stepSize' in value) || value['stepSize'] === undefined) return false;
    if (!('paymentDeadline' in value) || value['paymentDeadline'] === undefined) return false;
    if (!('cancellationDeadline' in value) || value['cancellationDeadline'] === undefined) return false;
    if (!('network' in value) || value['network'] === undefined) return false;
    if (!('code' in value) || value['code'] === undefined) return false;
    if (!('calculationFactor' in value) || value['calculationFactor'] === undefined) return false;
    if (!('issuanceDocumentId' in value) || value['issuanceDocumentId'] === undefined) return false;
    if (!('issuerId' in value) || value['issuerId'] === undefined) return false;
    if (!('issuerWalletAddress' in value) || value['issuerWalletAddress'] === undefined) return false;
    if (!('ewpg' in value) || value['ewpg'] === undefined) return false;
    if (!('publicationRestricted' in value) || value['publicationRestricted'] === undefined) return false;
    return true;
}

export function AdminProductCreationRequestFromJSON(json: any): AdminProductCreationRequest {
    return AdminProductCreationRequestFromJSONTyped(json, false);
}

export function AdminProductCreationRequestFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminProductCreationRequest {
    if (json == null) {
        return json;
    }
    return {
        
        'name': json['name'],
        'productType': ProductTypeEnumFromJSON(json['product_type']),
        'agioPercent': json['agio_percent'],
        'nominalValue': MoneyRequestFromJSON(json['nominal_value']),
        'issuancePrice': MoneyRequestFromJSON(json['issuance_price']),
        'provideNominalValue': json['provide_nominal_value'],
        'maxIssuableAmount': json['max_issuable_amount'],
        'stepSize': json['step_size'],
        'paymentDeadline': json['payment_deadline'],
        'cancellationDeadline': json['cancellation_deadline'],
        'network': NetworkNameTechnicalEnumFromJSON(json['network']),
        'code': json['code'],
        'calculationFactor': json['calculation_factor'],
        'backupWallet': json['backup_wallet'] == null ? undefined : json['backup_wallet'],
        'lockInTime': json['lock_in_time'] == null ? undefined : (new Date(json['lock_in_time'])),
        'issuanceDocumentId': json['issuance_document_id'],
        'issuerId': json['issuer_id'],
        'issuerWalletAddress': json['issuer_wallet_address'],
        'ewpg': json['ewpg'],
        'isin': json['isin'] == null ? undefined : json['isin'],
        'publicationRestricted': json['publication_restricted'],
    };
}

export function AdminProductCreationRequestToJSON(value?: AdminProductCreationRequest | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'name': value['name'],
        'product_type': ProductTypeEnumToJSON(value['productType']),
        'agio_percent': value['agioPercent'],
        'nominal_value': MoneyRequestToJSON(value['nominalValue']),
        'issuance_price': MoneyRequestToJSON(value['issuancePrice']),
        'provide_nominal_value': value['provideNominalValue'],
        'max_issuable_amount': value['maxIssuableAmount'],
        'step_size': value['stepSize'],
        'payment_deadline': value['paymentDeadline'],
        'cancellation_deadline': value['cancellationDeadline'],
        'network': NetworkNameTechnicalEnumToJSON(value['network']),
        'code': value['code'],
        'calculation_factor': value['calculationFactor'],
        'backup_wallet': value['backupWallet'],
        'lock_in_time': value['lockInTime'] == null ? undefined : ((value['lockInTime']).toISOString()),
        'issuance_document_id': value['issuanceDocumentId'],
        'issuer_id': value['issuerId'],
        'issuer_wallet_address': value['issuerWalletAddress'],
        'ewpg': value['ewpg'],
        'isin': value['isin'],
        'publication_restricted': value['publicationRestricted'],
    };
}

